/**
 * <poi-location-preview> — the Invisual exhibitor stand-location banner, as a link.
 *
 * The whole widget is one <img> inside one <a>: the image comes from the public
 * banner endpoint, the link opens the interactive map on the exhibitor's stand.
 * No dependencies, no network calls of our own — the browser's image loader does
 * the fetching, which is also why no CORS headers are involved.
 *
 * The endpoint 404s routinely (stands get assigned late, some exhibitors never get
 * one), so a failed load hides the element outright rather than showing an error.
 */

const TAG = 'poi-location-preview';
const DEFAULT_API_BASE = 'https://api.invisual.app';
const BANNER_PATH = '/v1/poi-banners/location-preview/';
const MAP_LINK_PATH = 'places/by-vendor-id';
const DEFAULT_ALT = 'Find our stand on the venue map';

const template = document.createElement('template');
template.innerHTML = `
<style>
  :host {
    display: block;
    width: 100%;
    line-height: 0;                      /* no descender gap under the image */
    -webkit-tap-highlight-color: transparent;
  }

  /* Nothing to show and nothing to say about it: no placeholder, no error state. */
  :host([state="idle"]),
  :host([state="error"]),
  :host([hidden]) { display: none !important; }

  a {
    position: relative;
    display: block;
    width: 100%;
    max-width: var(--invisual-banner-max-width, var(--_natural-width, none));
    /* Holds the box before the image arrives; released once it has, so the real
       aspect ratio governs and nothing is ever cropped. */
    aspect-ratio: var(--invisual-banner-ratio, var(--_natural-ratio, 2 / 1));
    overflow: hidden;
    border-radius: var(--invisual-banner-radius, 12px);
    outline-offset: 3px;
    transition: filter .18s ease, transform .18s ease;
  }
  :host([state="ready"]) a { aspect-ratio: auto; }
  a:hover { filter: brightness(1.04); }
  a:active { transform: scale(.995); }

  img {
    display: block;
    width: 100%;
    height: auto;
    border: 0;
    opacity: 0;
    transition: opacity .25s ease;
  }
  :host([state="ready"]) img { opacity: 1; }

  .skeleton {
    position: absolute;
    inset: 0;
    background: var(--invisual-banner-skeleton-color, #e7ebf0);
    overflow: hidden;
  }
  .skeleton::after {
    content: "";
    position: absolute;
    inset: 0;
    transform: translateX(-100%);
    background: linear-gradient(90deg,
      transparent 0%,
      rgba(255, 255, 255, .55) 50%,
      transparent 100%);
    animation: shimmer 1.4s infinite;
  }
  :host([state="ready"]) .skeleton { display: none; }

  @keyframes shimmer { to { transform: translateX(100%); } }

  @media (prefers-reduced-motion: reduce) {
    a, img { transition: none; }
    a:active { transform: none; }
    .skeleton::after { animation: none; }
  }
</style>
<a part="link" target="_blank" rel="noopener noreferrer">
  <img part="image" decoding="async" alt="">
  <span class="skeleton" part="skeleton" aria-hidden="true"></span>
</a>
`;

/** Attribute -> property mirror, so JS callers can set either. */
const PROPS = {
  mapDomain: 'map-domain',
  poiVendorId: 'poi-vendor-id',
  apiBase: 'api-base',
  href: 'href',
  alt: 'alt',
  loading: 'loading',
  w: 'w',
  h: 'h',
  scale: 'scale',
};

/** A positive whole number, or null when the attribute is absent or nonsense. */
function positiveInt(value, name) {
  if (value === null || value === '') return null;
  const number = Number(value);
  if (!Number.isInteger(number) || number <= 0) {
    console.warn(`<${TAG}>: ignoring "${name}" — expected a positive whole number, got "${value}".`);
    return null;
  }
  return number;
}

function bannerSrc({ apiBase, mapDomain, vendorId, w, h, scale }) {
  const base = (apiBase || DEFAULT_API_BASE).replace(/\/+$/, '');
  // Resolved against the page so a relative api-base ("/api") works behind a proxy.
  const url = new URL(base + BANNER_PATH, document.baseURI);
  url.searchParams.set('map_domain', mapDomain);
  url.searchParams.set('vendor_id', vendorId);
  if (w) url.searchParams.set('w', String(w));
  if (h) url.searchParams.set('h', String(h));
  if (scale) url.searchParams.set('scale', String(scale));
  return url.toString();
}

/** Deep link to the exhibitor on the interactive map; the map redirects it to the stand preview. */
function mapHref(mapDomain, vendorId) {
  const host = mapDomain.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  return `https://${host}/${MAP_LINK_PATH}/${encodeURIComponent(vendorId)}`;
}

export class PoiLocationPreview extends HTMLElement {
  static get observedAttributes() {
    return ['map-domain', 'poi-vendor-id', 'w', 'h', 'scale', 'api-base', 'href', 'alt', 'loading'];
  }

  constructor() {
    super();
    const root = this.attachShadow({ mode: 'open' });
    root.appendChild(template.content.cloneNode(true));
    this._link = root.querySelector('a');
    this._img = root.querySelector('img');
    this._src = '';
    this._token = 0;
    this._queued = false;
  }

  connectedCallback() {
    this._schedule();
  }

  attributeChangedCallback() {
    if (this.isConnected) this._schedule();
  }

  /** Re-request the banner, even if nothing about it changed. */
  reload() {
    this._src = '';
    this._schedule();
  }

  /** The banner endpoint URL currently in use, or '' before it can be built. */
  get imageSrc() {
    return this._src;
  }

  /** Where a click goes. */
  get linkHref() {
    return this._link.getAttribute('href') || '';
  }

  /** Several attributes are usually set in a row; render once when they settle. */
  _schedule() {
    if (this._queued) return;
    this._queued = true;
    Promise.resolve().then(() => {
      this._queued = false;
      if (this.isConnected) this._render();
    });
  }

  _render() {
    const mapDomain = (this.getAttribute('map-domain') || '').trim();
    const vendorId = (this.getAttribute('poi-vendor-id') || '').trim();
    if (!mapDomain || !vendorId) {
      // Half-configured, most likely mid-hydration in a framework. Say nothing, show nothing.
      this._src = '';          // so re-configuring with the previous values renders again
      this.setAttribute('state', 'idle');
      return;
    }

    const w = positiveInt(this.getAttribute('w'), 'w');
    const h = positiveInt(this.getAttribute('h'), 'h');
    const scale = positiveInt(this.getAttribute('scale'), 'scale');

    this._link.setAttribute('href', (this.getAttribute('href') || '').trim() || mapHref(mapDomain, vendorId));
    this._img.setAttribute('alt', (this.getAttribute('alt') || '').trim() || DEFAULT_ALT);
    this._img.setAttribute('loading', this.getAttribute('loading') === 'eager' ? 'eager' : 'lazy');

    // Hints for the pre-load box; both are defaults a stylesheet can still override.
    this.style.setProperty('--_natural-width', w ? `${w}px` : 'none');
    this.style.setProperty('--_natural-ratio', w && h ? `${w} / ${h}` : '2 / 1');

    const src = bannerSrc({ apiBase: (this.getAttribute('api-base') || '').trim(), mapDomain, vendorId, w, h, scale });
    if (src === this._src) return;

    this._src = src;
    this._token += 1;
    const token = this._token;
    this.setAttribute('state', 'loading');
    this._img.onload = () => { if (token === this._token) this._loaded(src); };
    this._img.onerror = () => { if (token === this._token) this._failed(src); };
    this._img.src = src;
  }

  _loaded(src) {
    this.setAttribute('state', 'ready');
    this._emit('invisual-banner-load', {
      src,
      width: this._img.naturalWidth,
      height: this._img.naturalHeight,
    });
  }

  _failed(src) {
    // An <img> reports no status code, so the reason is unknowable here. In practice
    // it is a 404: the exhibitor has no stand on the map yet.
    this.setAttribute('state', 'error');
    this._emit('invisual-banner-error', { src });
  }

  _emit(type, detail) {
    this.dispatchEvent(new CustomEvent(type, { detail, bubbles: true, composed: true }));
  }
}

for (const [prop, attribute] of Object.entries(PROPS)) {
  Object.defineProperty(PoiLocationPreview.prototype, prop, {
    get() {
      return this.getAttribute(attribute);
    },
    set(value) {
      if (value === null || value === undefined || value === '') this.removeAttribute(attribute);
      else this.setAttribute(attribute, String(value));
    },
    enumerable: true,
    configurable: true,
  });
}

if (!customElements.get(TAG)) customElements.define(TAG, PoiLocationPreview);
